import { SocialEmbedIframe } from '@bridebook/web/app-shared/components/articles/embeds/social-embed-iframe';
import {
  getInstagramEmbedSrc,
  getSpotifyEmbedSrcFromUrl,
  getTiktokEmbedSrc,
  getTiktokEmbedSrcFromUrl,
  getYoutubeEmbedSrcFromUrl,
  getYoutubeShortEmbedSrc,
  hostMatches,
  INSTAGRAM_EMBED_DEFAULT_HEIGHT,
  INSTAGRAM_EMBED_MAX_WIDTH_CLASSNAME,
  resolveTiktokEmbedSrc,
  SPOTIFY_EMBED_DEFAULT_HEIGHT,
  SPOTIFY_EMBED_MAX_WIDTH_CLASSNAME,
  SPOTIFY_HOST,
  TIKTOK_EMBED_DEFAULT_HEIGHT,
  TIKTOK_EMBED_MAX_WIDTH_CLASSNAME,
  YOUTUBE_EMBED_MAX_WIDTH_CLASSNAME,
  YOUTUBE_SHORT_EMBED_DEFAULT_HEIGHT,
  YOUTUBE_SHORT_EMBED_MAX_WIDTH_CLASSNAME,
} from '@bridebook/web/app-shared/components/articles/embeds/social-embed-utils';
import type { DataNode, Element, Node } from '@bridebook/web/types/dom';

const getMeaningfulChildren = (node: Element): Node[] =>
  (node.children || []).filter(
    (child) =>
      child.type === 'tag' ||
      (child.type === 'text' && !!(child as unknown as DataNode).data?.trim()),
  );

const getSingleElementChild = (node: Element): Element | null => {
  const meaningful = getMeaningfulChildren(node);
  return meaningful.length === 1 && meaningful[0].type === 'tag'
    ? (meaningful[0] as Element)
    : null;
};

const unwrapEmbedWrapper = (node: Element): Element => {
  if (node.type !== 'tag') return node;

  if (node.name === 'p') {
    const child = getSingleElementChild(node);
    return child ? unwrapEmbedWrapper(child) : node;
  }

  if (node.name === 'figure' && (node.attribs?.class || '').includes('wp-block-embed')) {
    const wrapper = getSingleElementChild(node);
    const isEmbedWrapperDiv =
      wrapper?.name === 'div' && (wrapper.attribs?.class || '').includes('wp-block-embed__wrapper');
    const embed = isEmbedWrapperDiv ? getSingleElementChild(wrapper) : null;
    if (embed) return embed;
  }

  return node;
};

const getBareUrlText = (node: Element): string | null => {
  if (node.type !== 'tag' || node.name !== 'p') return null;
  const meaningful = getMeaningfulChildren(node);
  if (meaningful.length !== 1 || meaningful[0].type !== 'text') return null;
  const text = (meaningful[0] as unknown as DataNode).data.trim();
  return /^https?:\/\//.test(text) ? text : null;
};

const resolveEmbedUrl = (node: Element): string | null => {
  const target = unwrapEmbedWrapper(node);
  if (target.type === 'tag' && target.name === 'a' && target.attribs?.href) {
    return target.attribs.href;
  }
  return getBareUrlText(node);
};

interface SocialEmbedConfig {
  title: string;
  host: string;
  blockquoteClass?: string;
  defaultHeight?: number;
  // Scales the embed with its container (16:9) instead of using a fixed pixel height.
  responsiveAspectRatio?: boolean;
  maxWidthClassName: string;
  autoResize?: boolean;
  getEmbedSrc: (target: Element, url: string | null) => string | null;
  getHeight?: (target: Element) => number | null;
  // Fallback when getEmbedSrc can't produce a src synchronously (e.g. TikTok short links).
  resolveSrc?: (url: string) => Promise<string | null>;
}

const SOCIAL_EMBED_CONFIGS: SocialEmbedConfig[] = [
  {
    title: 'Spotify embed',
    host: SPOTIFY_HOST,
    defaultHeight: SPOTIFY_EMBED_DEFAULT_HEIGHT,
    maxWidthClassName: SPOTIFY_EMBED_MAX_WIDTH_CLASSNAME,
    getHeight: (target) =>
      target.type === 'tag' && target.name === 'iframe'
        ? Number(target.attribs?.height) || null
        : null,
    getEmbedSrc: (target, url) => {
      if (target.type === 'tag' && target.name === 'iframe' && target.attribs?.src) {
        return target.attribs.src;
      }
      return url && getSpotifyEmbedSrcFromUrl(url);
    },
  },
  {
    title: 'Instagram embed',
    host: 'instagram.com',
    blockquoteClass: 'instagram-media',
    defaultHeight: INSTAGRAM_EMBED_DEFAULT_HEIGHT,
    maxWidthClassName: INSTAGRAM_EMBED_MAX_WIDTH_CLASSNAME,
    autoResize: true,
    getEmbedSrc: (target, url) => {
      const permalink =
        target.type === 'tag' && target.name === 'blockquote'
          ? target.attribs?.['data-instgrm-permalink']
          : url;
      return permalink ? getInstagramEmbedSrc(permalink) : null;
    },
  },
  {
    title: 'TikTok embed',
    host: 'tiktok.com',
    blockquoteClass: 'tiktok-embed',
    defaultHeight: TIKTOK_EMBED_DEFAULT_HEIGHT,
    maxWidthClassName: TIKTOK_EMBED_MAX_WIDTH_CLASSNAME,
    getEmbedSrc: (target, url) => {
      const videoId =
        target.type === 'tag' && target.name === 'blockquote'
          ? target.attribs?.['data-video-id']
          : null;
      return videoId ? getTiktokEmbedSrc(videoId) : getTiktokEmbedSrcFromUrl(url || '');
    },
    resolveSrc: resolveTiktokEmbedSrc,
  },
  {
    title: 'YouTube Shorts embed',
    host: 'youtube.com',
    defaultHeight: YOUTUBE_SHORT_EMBED_DEFAULT_HEIGHT,
    maxWidthClassName: YOUTUBE_SHORT_EMBED_MAX_WIDTH_CLASSNAME,
    getEmbedSrc: (target, url) => {
      if (target.type === 'tag' && target.name === 'iframe' && target.attribs?.src) {
        const width = Number(target.attribs?.width);
        const height = Number(target.attribs?.height);
        const isPortrait = !!width && !!height && height > width;
        return isPortrait ? target.attribs.src : null;
      }
      return url ? getYoutubeShortEmbedSrc(url) : null;
    },
  },
  {
    title: 'YouTube embed',
    host: 'youtube.com',
    responsiveAspectRatio: true,
    maxWidthClassName: YOUTUBE_EMBED_MAX_WIDTH_CLASSNAME,
    getEmbedSrc: (target, url) => {
      if (target.type === 'tag' && target.name === 'iframe' && target.attribs?.src) {
        const width = Number(target.attribs?.width);
        const height = Number(target.attribs?.height);
        const isPortrait = !!width && !!height && height > width;
        // Portrait iframes (Shorts) are handled by the config above.
        return isPortrait ? null : target.attribs.src;
      }
      return url ? getYoutubeEmbedSrcFromUrl(url) : null;
    },
  },
];

const matchesSocialEmbedConfig = (
  target: Element,
  url: string | null,
  config: SocialEmbedConfig,
): boolean => {
  if (target.type === 'tag' && target.name === 'iframe') {
    return hostMatches(target.attribs?.src || '', config.host);
  }
  if (target.type === 'tag' && target.name === 'blockquote' && config.blockquoteClass) {
    return (target.attribs?.class || '').includes(config.blockquoteClass);
  }
  return !!url && hostMatches(url, config.host);
};

// Several configs can share a host (e.g. YouTube Shorts vs. regular YouTube), so a host match
// alone isn't enough — try each config in order until one actually produces a usable src.
const findSocialEmbedMatch = (
  node: Element,
): { config: SocialEmbedConfig; url: string | null; src: string | null } | null => {
  const target = unwrapEmbedWrapper(node);
  const url = resolveEmbedUrl(node);

  for (const config of SOCIAL_EMBED_CONFIGS) {
    if (!matchesSocialEmbedConfig(target, url, config)) continue;

    const src = config.getEmbedSrc(target, url);
    if (src || (config.resolveSrc && url)) return { config, url, src };
  }

  return null;
};

export const isSocialEmbed = (node: Element): boolean => !!findSocialEmbedMatch(node);

export const convertSocialEmbed = (node: Element, index: number) => {
  const match = findSocialEmbedMatch(node);
  if (!match) return null;

  const { config, url, src } = match;
  const target = unwrapEmbedWrapper(node);

  return (
    <SocialEmbedIframe
      key={index}
      src={src}
      unresolvedUrl={url ?? undefined}
      resolveSrc={config.resolveSrc}
      title={config.title}
      height={config.getHeight?.(target) ?? config.defaultHeight ?? 0}
      responsiveAspectRatio={config.responsiveAspectRatio}
      maxWidthClassName={config.maxWidthClassName}
      autoResize={config.autoResize}
    />
  );
};
